import { canSignKycForm } from '@dealstack/frontend/investor/pages/Investment/common/InvestorKycFormsTable/KycFormSignAction.utils';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import {
	hasSubmission,
	type FormPair,
} from '@dealstack/frontend/investor/pages/Investment/common/InvestorKycFormsTable/types';
import type { InheritedFormsDisplay } from './InheritedFormsConfirmation.types';

/**
 * Does this workflow still owe a signature? Uses `canSignKycForm`, the same predicate the table's
 * Sign action uses, over the narrow slice so raw `InvestorKycFormRes` callers need no re-mapping.
 */
export const hasFormAwaitingRequiredSignature = (
	forms: Pick<FormPair, 'requiresSignature' | 'signatureStatus' | 'signatureId'>[]
): boolean => forms.some(canSignKycForm);

/**
 * Does any form in this set still need the investor to act? Both ways that happens: awaiting a
 * required signature, or rejected/re-requested.
 */
export const hasFormNeedingInvestorAction = (forms: FormPair[]): boolean =>
	hasFormAwaitingRequiredSignature(forms) || forms.some((form) => isKycRejected(form.status));

/**
 * Whether the investor sees `InheritedFormsConfirmation` instead of `InvestorKycFormsTable`. That
 * screen offers no Sign and no redo, so any form needing investor action disqualifies the whole set.
 */
export const getInheritedFormsDisplay = ({
	forms,
	inheritedDismissed,
}: {
	forms: FormPair[];
	inheritedDismissed: boolean;
}): InheritedFormsDisplay => {
	const inheritedForms = forms.filter((form) => form.isInherited);
	const needsInvestorAction = hasFormNeedingInvestorAction(forms);
	const hasUnsubmittedInheritedForm = inheritedForms.some((form) => !hasSubmission(form));
	// `isInherited` is `subjectAction === NOT_YET_ACTED`; a form the investor has since confirmed or
	// re-submitted keeps a non-null subjectAction, which is what marks the set as inherited-in-origin.
	const hasAnyInheritedForms = forms.some((form) => form.subjectAction != null);
	const isAllFilled = forms.length > 0 && forms.every((form) => hasSubmission(form));

	return {
		inheritedForms,
		isInheritedAndLocked:
			inheritedForms.length > 0 &&
			!inheritedDismissed &&
			!needsInvestorAction &&
			!hasUnsubmittedInheritedForm,
		isConfirmedInherited:
			hasAnyInheritedForms &&
			inheritedForms.length === 0 &&
			!inheritedDismissed &&
			isAllFilled &&
			!needsInvestorAction,
	};
};
